import { Helmet } from 'react-helmet-async';
import React from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
}

export const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website'
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* OpenGraph Meta Tags */}
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={ogUrl || canonical} />
      <meta property="og:site_name" content="TyrantShield Technologies LLC" />
      <meta property="og:locale" content="en_US" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && <meta property="og:image:alt" content={ogTitle || title} />}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tyrantshield" />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </Helmet>
  );
};

// SEO data for each page
export const seoData = {
  home: {
    title: 'TyrantShield Technologies - IT & Cybersecurity Cheyenne WY',
    description: 'Leading IT company in Cheyenne, Wyoming. Enterprise cybersecurity, compliance & technology solutions for Wyoming businesses.',
    keywords: 'IT company Cheyenne, cybersecurity Wyoming, technology services Cheyenne WY, network security Wyoming, enterprise IT Cheyenne, compliance services Wyoming',
    canonical: 'https://tyrantshield.com/',
    ogTitle: 'TyrantShield Technologies - Premier IT & Cybersecurity in Cheyenne, WY',
    ogDescription: 'Leading IT company in Cheyenne, Wyoming. Enterprise cybersecurity, compliance & technology solutions for Wyoming businesses.',
    ogImage: 'https://tyrantshield.com/og-image-home.jpg',
    ogUrl: 'https://tyrantshield.com/'
  },
  about: {
    title: 'About TyrantShield Technologies - Cheyenne IT Security Experts | Wyoming',
    description: 'Learn about TyrantShield Technologies LLC, Cheyenne\'s premier IT company and cybersecurity provider. Over 20 years of Fortune 500 experience serving Wyoming businesses.',
    keywords: 'about TyrantShield Technologies, Cheyenne IT company, Wyoming cybersecurity experts, enterprise security Cheyenne, IT services Wyoming',
    canonical: 'https://tyrantshield.com/about',
    ogTitle: 'About TyrantShield Technologies - Wyoming\'s IT Security Experts',
    ogDescription: 'Learn about TyrantShield Technologies LLC, Cheyenne\'s premier IT company with over 20 years of Fortune 500 cybersecurity experience.',
    ogImage: 'https://tyrantshield.com/og-image-about.jpg',
    ogUrl: 'https://tyrantshield.com/about'
  },
  services: {
    title: 'Professional IT Services & Cybersecurity Solutions | Cheyenne Wyoming',
    description: 'Comprehensive IT support, cybersecurity services, and technology solutions for Wyoming businesses. Enterprise-grade security and compliance expertise in Cheyenne.',
    keywords: 'IT services Cheyenne, cybersecurity services Wyoming, enterprise IT support, network security Cheyenne, technology consulting Wyoming',
    canonical: 'https://tyrantshield.com/services',
    ogTitle: 'Professional IT Services & Cybersecurity Solutions in Cheyenne, WY',
    ogDescription: 'Comprehensive IT support, cybersecurity services, and technology solutions for Wyoming businesses. Enterprise-grade security and compliance expertise.',
    ogImage: 'https://tyrantshield.com/og-image-services.jpg',
    ogUrl: 'https://tyrantshield.com/services'
  },
  capabilities: {
    title: 'IT & Cybersecurity Technical Capabilities | Wyoming Technology Solutions',
    description: 'Explore our 12 enterprise-grade technical capabilities serving Wyoming businesses. Advanced cybersecurity, network design, and compliance solutions from Cheyenne experts.',
    keywords: 'technical capabilities Wyoming, cybersecurity expertise Cheyenne, IT solutions Wyoming, enterprise technology capabilities',
    canonical: 'https://tyrantshield.com/capabilities',
    ogTitle: '12 Enterprise-Grade IT & Cybersecurity Capabilities | Wyoming',
    ogDescription: 'Explore our comprehensive technical capabilities serving Wyoming businesses. Advanced cybersecurity, network design, and compliance solutions.',
    ogImage: 'https://tyrantshield.com/og-image-capabilities.jpg',
    ogUrl: 'https://tyrantshield.com/capabilities'
  },
  team: {
    title: 'Our Expert IT & Cybersecurity Team | TyrantShield Technologies Wyoming',
    description: 'Meet the experienced IT and cybersecurity professionals at TyrantShield Technologies. Fortune 500 expertise serving Wyoming businesses from our Cheyenne headquarters.',
    keywords: 'IT team Wyoming, cybersecurity experts Cheyenne, technology professionals Wyoming, enterprise IT specialists',
    canonical: 'https://tyrantshield.com/team',
    ogTitle: 'Expert IT & Cybersecurity Team | TyrantShield Technologies Wyoming',
    ogDescription: 'Meet our experienced IT and cybersecurity professionals with Fortune 500 expertise serving Wyoming businesses from Cheyenne.',
    ogImage: 'https://tyrantshield.com/og-image-team.jpg',
    ogUrl: 'https://tyrantshield.com/team'
  },
  contact: {
    title: 'Contact Wyoming\'s Premier IT Company | TyrantShield Technologies Cheyenne',
    description: 'Contact TyrantShield Technologies for professional IT support and cybersecurity services in Cheyenne, Wyoming. Free consultations for Wyoming businesses.',
    keywords: 'contact IT company Cheyenne, Wyoming cybersecurity consultation, IT support contact Wyoming, technology services Cheyenne',
    canonical: 'https://tyrantshield.com/contact',
    ogTitle: 'Contact Wyoming\'s Premier IT Company | TyrantShield Technologies',
    ogDescription: 'Contact TyrantShield Technologies for professional IT support and cybersecurity services in Cheyenne, Wyoming. Free consultations available.',
    ogImage: 'https://tyrantshield.com/og-image-contact.jpg',
    ogUrl: 'https://tyrantshield.com/contact'
  },
  book: {
    title: 'Schedule IT Consultation | Free Cybersecurity Assessment Cheyenne WY',
    description: 'Book a free IT consultation and cybersecurity assessment with Wyoming\'s premier technology experts. Professional IT services for Cheyenne area businesses.',
    keywords: 'book IT consultation Wyoming, free cybersecurity assessment Cheyenne, schedule IT services Wyoming, technology consultation',
    canonical: 'https://tyrantshield.com/book',
    ogTitle: 'Schedule Free IT Consultation | Cybersecurity Assessment Cheyenne WY',
    ogDescription: 'Book a free IT consultation and cybersecurity assessment with Wyoming\'s premier technology experts. Professional services for Cheyenne businesses.',
    ogImage: 'https://tyrantshield.com/og-image-book.jpg',
    ogUrl: 'https://tyrantshield.com/book'
  },
  privacy: {
    title: 'Privacy Policy | TyrantShield Technologies Wyoming IT Company',
    description: 'Privacy policy for TyrantShield Technologies LLC, Wyoming\'s premier IT company and cybersecurity provider serving Cheyenne and surrounding areas.',
    keywords: 'privacy policy Wyoming IT company, TyrantShield privacy, cybersecurity privacy policy',
    canonical: 'https://tyrantshield.com/privacy',
    ogTitle: 'Privacy Policy | TyrantShield Technologies Wyoming',
    ogDescription: 'Privacy policy for TyrantShield Technologies LLC, Wyoming\'s premier IT company and cybersecurity provider.',
    ogImage: 'https://tyrantshield.com/og-image-privacy.jpg',
    ogUrl: 'https://tyrantshield.com/privacy'
  },
  terms: {
    title: 'Terms of Service | TyrantShield Technologies Wyoming',
    description: 'Terms of service for TyrantShield Technologies LLC, professional IT and cybersecurity services provider in Cheyenne, Wyoming.',
    keywords: 'terms of service Wyoming IT, TyrantShield terms, cybersecurity terms Wyoming',
    canonical: 'https://tyrantshield.com/terms',
    ogTitle: 'Terms of Service | TyrantShield Technologies Wyoming',
    ogDescription: 'Terms of service for TyrantShield Technologies LLC, professional IT and cybersecurity services provider in Cheyenne, Wyoming.',
    ogImage: 'https://tyrantshield.com/og-image-terms.jpg',
    ogUrl: 'https://tyrantshield.com/terms'
  },
  hardwareSales: {
    title: 'Enterprise Hardware Sales | IT Equipment Wyoming | TyrantShield Technologies',
    description: 'Complete range of enterprise IT hardware from leading manufacturers. Servers, networking equipment, security appliances, and workstations for Wyoming businesses.',
    keywords: 'hardware sales Wyoming, enterprise servers Cheyenne, networking equipment Wyoming, IT hardware sales, business computers Wyoming',
    canonical: 'https://tyrantshield.com/hardware-sales',
    ogTitle: 'Enterprise Hardware Sales | IT Equipment for Wyoming Businesses',
    ogDescription: 'Complete range of enterprise IT hardware from leading manufacturers. Servers, networking equipment, and security appliances for Wyoming businesses.',
    ogImage: 'https://tyrantshield.com/og-image-hardware.jpg',
    ogUrl: 'https://tyrantshield.com/hardware-sales'
  },
  softwareLicensing: {
    title: 'Software Licensing Solutions | Microsoft & Enterprise Software Wyoming | TyrantShield',
    description: 'Software licenses and solutions from major technology vendors. Microsoft licensing, security software, backup solutions, and virtualization software for Wyoming businesses.',
    keywords: 'software licensing Wyoming, Microsoft licensing Cheyenne, enterprise software Wyoming, business software licenses, IT software solutions',
    canonical: 'https://tyrantshield.com/software-licensing',
    ogTitle: 'Software Licensing Solutions | Microsoft & Enterprise Software Wyoming',
    ogDescription: 'Software licenses from major technology vendors. Microsoft licensing, security software, and enterprise solutions for Wyoming businesses.',
    ogImage: 'https://tyrantshield.com/og-image-software.jpg',
    ogUrl: 'https://tyrantshield.com/software-licensing'
  },
  adaptiveAIDefense: {
    title: 'Patent-Pending AI Defense Framework | Adaptive eBPF Cybersecurity | Cheyenne Wyoming',
    description: 'Revolutionary patent-pending Adaptive eBPF Behavioral Monitoring System from TyrantShield Technologies. Advanced AI-driven cybersecurity innovation developed in Cheyenne, Wyoming for next-generation threat detection.',
    keywords: 'patent pending AI defense, eBPF cybersecurity, adaptive AI framework, behavioral monitoring system, AI cybersecurity Cheyenne Wyoming, patent technology TyrantShield, kernel level security, encrypted payload detection',
    canonical: 'https://tyrantshield.com/adaptive-ai-defense',
    ogTitle: 'Patent-Pending Adaptive AI Defense Framework | Revolutionary Cybersecurity Innovation',
    ogDescription: 'Revolutionary patent-pending Adaptive eBPF Behavioral Monitoring System. Advanced AI-driven cybersecurity innovation from Cheyenne, Wyoming detecting threats in encrypted payloads.',
    ogImage: 'https://tyrantshield.com/og-image-ai-defense.jpg',
    ogUrl: 'https://tyrantshield.com/adaptive-ai-defense'
  }
};